import {
  ParlorHeadSection,
  CTypeMainHead,
  ATypeCardView,
  DTypeParlorList,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  ParlorDetailProps,
  numberToLocalString,
  CTypeTabs,
  DTypeBinsoPriceSummary,
  GOI_WEB_URL,
  ATypeCTA,
  BTypeBreadCrumb,
  DTypeCTA,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";
import parlors from "goi_common/src/constants/parlors_detail.json";

import React from "react";

import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";

const { regionColor, targetSidos } = parlorRegion;

function getByValue(map: Map<number, any>, searchValue: any) {
  for (const [key, value] of map.entries()) {
    if (key === searchValue) return value;
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = parlors
    .filter((res) => targetSidos.includes(res.sido))
    .map((parlor) => ({
      params: {
        companyname:
          getByValue(FUNERAL_SIDO, Number(parlor.sido)) +
          " " +
          getByValue(
            FUNERAL_SIGUNGU.get(Number(parlor.sido)) as any,
            Number(parlor.sigungu)
          ) +
          " 의 " +
          parlor.companyname,
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const code = params?.companyname as string;
  const companyname = code.split(" 의 ")[1];
  let parlor;
  let district;
  let name = "";
  let description = "";

  if (companyname) {
    parlor = parlors.find((parlor) => parlor.companyname === companyname);
    if (parlor) {
      name = parlor.companyname;

      district = parlor.address.slice(0, 2);
      if (parlor.address.split(" ")[0].length == 4)
        district = parlor.address[0] + parlor.address[2];

      let binsoString = "";
      parlor.binsos.forEach((binso: any) => {
        binsoString += `${binso.pyeong.toFixed()}평-${numberToLocalString(
          binso.charge
        )}원, `;
      });

      description = `${parlor.companyname}의 빈소 평수, 24시간 이용 기준 빈소 이용료 안내.`;
    }
  }

  return {
    props: {
      defaultParlor: parlor,
      pageDescription: description,
      pageKeywords: `${parlor?.companyname}, ${parlor?.companyname} 비용, ${parlor?.companyname}평균 예상 비용, ${parlor?.companyname} 무빈소, ${parlor?.companyname} 가족장, ${parlor?.companyname} 추천,
      ${parlor?.companyname} 빈소, ${parlor?.companyname} 후기, ${parlor?.companyname} 얼마`,
      pageOgTitle: `(${district}) ${parlor?.companyname} 비용 요약 안내 :: 충청대전세종장례소개포털`,
      pageOgDescription: description,
      pageOgImage: parlor?.images[0]?.url || "",
      hasError: !parlor,
    },
  };
};

export default function price({
  defaultParlor,
  pageOgTitle,
}: {
  defaultParlor: ParlorDetailProps;
  pageOgTitle: string;
}) {
  const regionParlors = parlors.filter((res) => targetSidos.includes(res.sido));
  const sidoName = FUNERAL_SIDO.get(Number(defaultParlor.sido));
  const sigunguName = FUNERAL_SIGUNGU.get(Number(defaultParlor.sido))?.get(
    Number(defaultParlor.sigungu)
  );

  const carouselItems = regionParlors.map((res, i) => {
    return {
      "@type": "ListItem",
      name: `(비용안내) ${res.companyname}`,
      image: `${res.images[0].url}`,
      url: `https://www.ch-funeralportal.co.kr/parlor-item/${sidoName} ${sigunguName} 의 ${defaultParlor.companyname}/`,
      position: i + 1,
    };
  });

  const carouselRichSearch =
    regionParlors.length > 6
      ? {
          "@context": "http://schema.org",
          "@type": "ItemList",
          itemListElement: carouselItems,
        }
      : {};

  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>
        {regionParlors.length > 5 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(carouselRichSearch),
            }}
          />
        )}
      </Head>

      <BTypeBreadCrumb
        firstItem={{
          text: `장례식장 추천`,
          href: GOI_WEB_URL + "/match/request/",
          event: {
            domain: "ch",
            path: "prlr_item_price",
            trigger_and_target: "br_crumb_mth_req",
          },
        }}
        secondItem={{
          text: `${sidoName} 장례식장`,
          href: `/parlors-list/${sidoName} 의 장례식장 목록/`,
          event: {
            domain: "ch",
            path: "prlr_item_price",
            trigger_and_target: "br_crumb_sido_prlr_list",
          },
        }}
        thirdItem={{
          text: `${sigunguName} 장례식장`,
          href: `/parlors-list/${sidoName}-${sigunguName} 의 장례식장 목록/`,
          event: {
            domain: "ch",
            path: "prlr_item_price",
            trigger_and_target: "br_crumb_sigungu_prlr_list",
          },
        }}
        goiHomeEvent={{
          domain: "ch",
          path: "prlr_item_price",
          trigger_and_target: "br_crumb_goi_home",
        }}
      />
      <ParlorHeadSection
        component={
          <>
            <CTypeMainHead
              sidoName={sidoName}
              sigunguName={sigunguName}
              mainColor={regionColor}
              defaultParlor={defaultParlor}
              title={`${defaultParlor.companyname} 비용`}
              managed_type={defaultParlor.managed_type}
              event={{
                domain: "ch",
                path: "prlr_item_price",
                trigger_and_target: "uv_mth_req",
              }}
            />
          </>
        }
      />

      <Stack
        style={{
          background: regionColor.c100,
          padding: "40px 20px",
        }}
      >
        {/* <ATypeCTA
          mainColor={regionColor}
          event={{
            domain: "ch",
            path: "prlr_item_price",
            trigger_and_target: "CTA_uv_mth_req",
          }}
        /> */}
        <DTypeCTA
          sidoName={sidoName}
          sigunguName={sigunguName}
          mainColor={regionColor}
          companyname={defaultParlor.companyname}
          href={`${GOI_WEB_URL}/match/parlor-requests/${defaultParlor.id}/`}
          event={{
            domain: "ch",
            path: "prlr_item_price",
            trigger_and_target: "CTA_uv_mth_req",
          }}
        />
      </Stack>

      <Stack>
        <CTypeTabs
          mainColor={regionColor}
          href={`parlor-item/${sidoName} ${sigunguName} 의 ${defaultParlor.companyname}`}
          selectTab="비용"
          defaultParlor={defaultParlor}
          event={{
            domain: "ch",
            path: "prlr_item_price",
            trigger_and_target: "CType_tab",
          }}
        />
      </Stack>

      <Stack
        style={{
          background: "white",
          padding: "40px 20px 20px",
        }}
      >
        <DTypeBinsoPriceSummary
          defaultParlor={defaultParlor}
          mainColor={regionColor}
          event={{
            domain: "ch",
            path: "prlr_item_price",
            trigger_and_target: "uv_expert_mth_req",
          }}
        />
      </Stack>
      <Stack
        style={{
          background: regionColor.c100,
          padding: "40px 20px 20px",
        }}
      >
        <DTypeParlorList
          binsoAveragePrice
          sido={defaultParlor.sido}
          title={`${sidoName}의 다른 장례식장`}
          CardViewType={(res) => (
            <ATypeCardView
              key={res.companyname}
              name={res.companyname}
              href={`/parlor-item/${sidoName} ${sigunguName} 의 ${defaultParlor.companyname}/`}
              descriptions={[
                `빈소 평균 비용 : ${Number(
                  res.binso_average_charge
                ).toLocaleString()}원`,
              ]}
              imgSrc={res.images[0] ? res.images[0].url : ""}
              event={{
                domain: "ch",
                path: "prlr_item_price",
                trigger_and_target: "uv_prlr_detail",
              }}
            />
          )}
        />
      </Stack>
    </>
  );
}
